"use client";

import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { MessageSquarePlus, Star } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { useLanguage } from "@/context/LanguageContext";
import type { PublicReview, PublicReviewPayload } from "@/types";
import {
  dismissToastLater,
  fetchPublicReviews,
  showLoadingToast,
  submitPublicReview,
} from "@/utils";

type ReviewFormState = PublicReviewPayload;

const emptyForm: ReviewFormState = {
  name: "",
  stars: 0,
  review: "",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function normalizeReview(value: unknown, index: number): PublicReview | null {
  if (!isRecord(value)) {
    return null;
  }

  const name = typeof value.name === "string" ? value.name.trim() : "";
  const review =
    typeof value.review === "string"
      ? value.review.trim()
      : typeof value.message === "string"
        ? value.message.trim()
        : "";
  const stars = Number(value.stars ?? value.rating ?? 5);

  if (!name || !review) {
    return null;
  }

  return {
    id: typeof value.id === "number" ? value.id : index,
    name,
    review,
    stars: Number.isFinite(stars)
      ? Math.min(Math.max(Math.round(stars), 0), 5)
      : 5,
    createdAt:
      typeof value.createdAt === "string" ? value.createdAt : undefined,
    createdAtFormatted:
      typeof value.createdAtFormatted === "string"
        ? value.createdAtFormatted
        : undefined,
  };
}

function normalizeReviews(data: unknown): PublicReview[] {
  let candidate: unknown[] = [];

  if (Array.isArray(data)) {
    candidate = data;
  } else if (isRecord(data) && Array.isArray(data.reviews)) {
    candidate = data.reviews;
  } else if (
    isRecord(data) &&
    isRecord(data.data) &&
    Array.isArray(data.data.reviews)
  ) {
    candidate = data.data.reviews;
  } else if (isRecord(data) && Array.isArray(data.items)) {
    candidate = data.items;
  } else if (isRecord(data) && Array.isArray(data.data)) {
    candidate = data.data;
  }

  return candidate
    .map((item, index) => normalizeReview(item, index))
    .filter((item): item is PublicReview => item !== null);
}

function shouldOpenReviewDialogFromHash(dialogOnly: boolean) {
  if (typeof window === "undefined") {
    return false;
  }

  const hash = window.location.hash.replace(/^#/, "");
  const [sectionId, queryString = ""] = hash.split("?");

  if (sectionId !== "recenze") {
    return false;
  }

  return dialogOnly
    ? queryString === ""
    : new URLSearchParams(queryString).get("open") === "true";
}

function setReviewHash(open: boolean, clearOnClose = false) {
  if (typeof window === "undefined") {
    return;
  }

  const hash = open ? "#recenze?open=true" : clearOnClose ? "" : "#recenze";
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${window.location.search}${hash}`,
  );
}

function ReviewStars({
  value,
  label,
  onChange,
}: {
  value: number;
  label?: string;
  onChange?: (value: number) => void;
}) {
  return (
    <div className="flex items-center gap-1" aria-label={label}>
      {[1, 2, 3, 4, 5].map((star) => {
        const isActive = star <= value;

        return onChange ? (
          <Button
            key={star}
            type="button"
            variant="ghost"
            size="icon"
            className="hover:scale-110"
            aria-label={`${star}/5`}
            onClick={() => onChange(star)}
          >
            <Star
              className={`size-5 ${
                isActive
                  ? "fill-yellow-400 text-yellow-400"
                  : "fill-gray-200 text-gray-200"
              }`}
              strokeWidth={0}
            />
          </Button>
        ) : (
          <Star
            key={star}
            className={`size-4 ${
              isActive
                ? "fill-yellow-400 text-yellow-400"
                : "fill-gray-200 text-gray-200"
            }`}
            strokeWidth={0}
            aria-hidden
          />
        );
      })}
    </div>
  );
}

export default function Reviews({
  dialogOnly = false,
}: {
  dialogOnly?: boolean;
}) {
  const { language } = useLanguage();
  const [reviews, setReviews] = useState<PublicReview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<ReviewFormState>(emptyForm);
  const [errorMessage, setErrorMessage] = useState("");

  const copy = useMemo(
    () =>
      ({
        cs: {
          label: "Recenze",
          title: "Co říkají uživatelé",
          subtitle:
            "Zkušenosti lidí, kterým SOFTIQ pomáhá hlídat realitní nabídky bez každodenního ručního hledání.",
          add: "Přidat recenzi",
          empty: "Recenze se zatím načítají nebo čekají na první zveřejnění.",
          loadError: "Recenze se nepodařilo načíst.",
          dialogTitle: "Přidat recenzi",
          name: "Jméno",
          namePlaceholder: "Vaše jméno",
          review: "Recenze",
          reviewPlaceholder: "Jaká je vaše zkušenost se SOFTIQ?",
          rating: "Vyberte hodnocení",
          cancel: "Zrušit",
          submit: "Odeslat recenzi",
          submitting: "Odesílám...",
          validation: "Vyplňte prosím jméno i recenzi.",
          submitted: "Děkujeme, recenze byla odeslána ke schválení.",
          submitError: "Recenzi se nepodařilo odeslat.",
        },
        en: {
          label: "Reviews",
          title: "What users say",
          subtitle:
            "Experiences from people who use SOFTIQ to monitor property listings without manual daily searching.",
          add: "Add review",
          empty:
            "Reviews are still loading or waiting for the first publication.",
          loadError: "Reviews could not be loaded.",
          dialogTitle: "Add review",
          dialogDescription:
            "Your review will be saved for approval and published after a quick check.",
          name: "Name",
          namePlaceholder: "Your name",
          review: "Review",
          reviewPlaceholder: "What was your experience with SOFTIQ?",
          rating: "Choose a rating",
          cancel: "Cancel",
          submit: "Submit review",
          submitting: "Submitting...",
          validation: "Please fill in your name and review.",
          submitted: "Thank you, your review was submitted for approval.",
          submitError: "The review could not be submitted.",
        },
      })[language],
    [language],
  );

  const loadReviews = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await fetchPublicReviews();

      if (response.status >= 200 && response.status < 300) {
        setReviews(normalizeReviews(response.data));
      } else {
        setErrorMessage(copy.loadError);
      }
    } catch (error) {
      console.error("Failed to load public reviews:", error);
      setErrorMessage(copy.loadError);
    } finally {
      setIsLoading(false);
    }
  }, [copy.loadError]);

  useEffect(() => {
    if (!dialogOnly) {
      void loadReviews();
    }
  }, [dialogOnly, loadReviews]);

  useEffect(() => {
    const syncDialogWithHash = () => {
      const shouldOpen = shouldOpenReviewDialogFromHash(dialogOnly);

      if (dialogOnly) {
        setIsDialogOpen(shouldOpen);
      } else if (shouldOpen) {
        setIsDialogOpen(true);
      }
    };

    syncDialogWithHash();
    window.addEventListener("hashchange", syncDialogWithHash);

    return () => window.removeEventListener("hashchange", syncDialogWithHash);
  }, [dialogOnly]);

  function openDialog() {
    setReviewHash(true);
    setIsDialogOpen(true);
  }

  function handleOpenChange(open: boolean) {
    setIsDialogOpen(open);

    if (!open) {
      setReviewHash(false, dialogOnly);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const payload = {
      name: form.name.trim(),
      stars: form.stars,
      review: form.review.trim(),
    };

    if (!payload.name || !payload.review) {
      toast.error(copy.validation);
      return;
    }

    const toastId = showLoadingToast(copy.submitting);
    setIsSubmitting(true);

    try {
      const response = await submitPublicReview(payload);

      if (response.status >= 200 && response.status < 300) {
        toast.success(copy.submitted);
        setForm(emptyForm);
        handleOpenChange(false);
        if (!dialogOnly) {
          void loadReviews();
        }
      } else {
        toast.error(copy.submitError);
      }
    } catch (error) {
      console.error("Failed to submit public review:", error);
      toast.error(copy.submitError);
    } finally {
      setIsSubmitting(false);
      dismissToastLater(toastId);
    }
  }

  return (
    <>
      {!dialogOnly ? (
        <div className="reviews-shell mx-auto max-w-6xl">
          <div className="reviews-header mb-7 flex flex-col items-center justify-between gap-4 text-center md:flex-row md:text-left">
            <div>
              <span className="section-label">{copy.label}</span>
              <h2 className="section-title text-softiq-copy dark:text-softiq-copy-inverse">
                {copy.title}
              </h2>
              <p className="section-subtitle md:mx-0">{copy.subtitle}</p>
            </div>
            <Button
              type="button"
              onClick={openDialog}
              className="reviews-add-button shrink-0"
            >
              <MessageSquarePlus className="size-4" aria-hidden />
              {copy.add}
            </Button>
          </div>

          {errorMessage ? (
            <p className="mx-auto max-w-xl text-center text-sm text-destructive">
              {errorMessage}
            </p>
          ) : null}

          {reviews.length > 0 ? (
            <div className="reviews-grid grid gap-4 md:grid-cols-3">
              {reviews.map((review, index) => (
                <article
                  key={`${review.id ?? index}-${review.name}`}
                  className="review-card text-left"
                >
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="app-content-title">
                      {review.name}
                    </h3>
                    <ReviewStars value={review.stars} />
                  </div>
                  <p className="app-content-description">
                    "{review.review}"
                  </p>
                </article>
              ))}
            </div>
          ) : !isLoading && !errorMessage ? (
            <div className="review-card mx-auto max-w-2xl text-center text-foreground/75">
              {copy.empty}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-3" aria-hidden>
              {[1, 2, 3].map((item) => (
                <div key={item} className="review-card !h-40 animate-pulse" />
              ))}
            </div>
          )}
        </div>
      ) : null}

      <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
        <DialogContent>
          <form className="grid gap-4" onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{copy.dialogTitle}</DialogTitle>
              <DialogDescription>{copy.dialogDescription}</DialogDescription>
            </DialogHeader>

            <label className="grid gap-1 text-sm font-medium text-foreground">
              {copy.name}
              <Input
                value={form.name}
                placeholder={copy.namePlaceholder}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
              />
            </label>

            <ReviewStars
              value={form.stars}
              label={copy.rating}
              onChange={(stars) =>
                setForm((current) => ({ ...current, stars }))
              }
            />

            <label className="grid gap-1 text-sm font-medium text-foreground">
              {copy.review}
              <Textarea
                value={form.review}
                placeholder={copy.reviewPlaceholder}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    review: event.target.value,
                  }))
                }
              />
            </label>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                {copy.cancel}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? copy.submitting : copy.submit}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
